import React, { useEffect, useState } from 'react';
import { RefreshCw, Eye, CheckCircle, AlertCircle, Star, FileText } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { getThumbnailUrl } from '~/utils/thumbnails';
import { createClientLogger } from '~/utils/logger.client';
import { IS_VIDEO_DISABLED } from './utils';

// Lazy logger creation to avoid SSR issues with mobile optimization
let logger: ReturnType<typeof createClientLogger> | null = null;
const getLogger = () => {
  if (!logger && typeof window !== 'undefined') {
    try {
      logger = createClientLogger('VideoSetting');
    } catch (error) {
      // Fallback for mobile devices with memory constraints
      console.warn('Failed to create logger, using console fallback:', error);
      return {
        info: (msg: string, data?: any) => console.info(`[VideoSetting] ${msg}`, data?.metadata || ''),
        warn: (msg: string, data?: any) => console.warn(`[VideoSetting] ${msg}`, data?.metadata || ''),
        error: (msg: string, data?: any) => console.error(`[VideoSetting] ${msg}`, data?.metadata || ''),
        debug: (msg: string, data?: any) => console.debug(`[VideoSetting] ${msg}`, data?.metadata || ''),
      };
    }
  }
  return logger || {
    info: (msg: string, data?: any) => console.info(`[VideoSetting] ${msg}`, data?.metadata || ''),
    warn: (msg: string, data?: any) => console.warn(`[VideoSetting] ${msg}`, data?.metadata || ''),
    error: (msg: string, data?: any) => console.error(`[VideoSetting] ${msg}`, data?.metadata || ''),
    debug: (msg: string, data?: any) => console.debug(`[VideoSetting] ${msg}`, data?.metadata || ''),
  };
};

interface VideoSettingProps {
  analysisData: any;
  thumbnailKey?: string | null;
  demoThumbnailUrl?: string;
}

export function VideoSetting({ analysisData, thumbnailKey, demoThumbnailUrl }: VideoSettingProps) {
  // Early return if video analysis is disabled
  //if (IS_VIDEO_DISABLED) {
   // return null;
    // return (
    //   <Card id="visual">
    //     <CardHeader>
    //       <CardTitle className="flex items-center gap-2">
    //         <Eye className="h-5 w-5" />
    //         Visual Setting Analysis
    //       </CardTitle>
    //       <CardDescription>
    //         Visual setting analysis is currently disabled
    //       </CardDescription>
    //     </CardHeader>
    //     <CardContent>
    //       <div className="text-center py-8">
    //         <div className="flex items-center justify-center gap-2 text-muted-foreground mb-4">
    //           <Eye className="h-5 w-5" />
    //           <span className="text-sm font-medium">
    //             Visual Analysis Disabled
    //           </span>
    //         </div>
    //         <p className="text-xs text-muted-foreground">
    //           Visual setting analysis features are currently disabled
    //         </p>
    //       </div>
    //     </CardContent>
    //   </Card>
    // );
  //}

  const [visualAnalysis, setVisualAnalysis] = useState<{
    goodPoints: string[];
    badPoints: string[];
    suggestions: string[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const hasAttemptedThumbnailGeneration = React.useRef(false);

  // Get thumbnail from video file in the files array
  const videoFile = analysisData?.files?.find((f: any) => 
    f.key && !f.key.includes('-audio-encrypted') && 
    (f.type?.startsWith('video/') || f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov'))
  );
  
  const thumbnailUrl = demoThumbnailUrl || getThumbnailUrl(
    thumbnailKey || // Explicit override
    videoFile?.thumbnailKey || // From video file in files array
    analysisData?.file?.thumbnailKey // Legacy fallback
  );
  
  // Check if visual analysis already exists in the database using dedicated fields
  const existingVisualAnalysis = analysisData?.visualSettingAnalyzedAt ? {
    goodPoints: Array.isArray(analysisData.visualSettingGoodPoints) ? analysisData.visualSettingGoodPoints : [],
    badPoints: Array.isArray(analysisData.visualSettingBadPoints) ? analysisData.visualSettingBadPoints : [],
    suggestions: Array.isArray(analysisData.visualSettingSuggestions) ? analysisData.visualSettingSuggestions : [],
    analyzedAt: analysisData.visualSettingAnalyzedAt,
  } : null;

  getLogger().info('VideoSetting render state', {
    metadata: {
      hasAnalyzed,
      hasExistingVisualAnalysis: !!existingVisualAnalysis,
      visualSettingAnalyzedAt: analysisData?.visualSettingAnalyzedAt,
      visualSettingGoodPoints: Array.isArray(analysisData?.visualSettingGoodPoints) ? analysisData.visualSettingGoodPoints.length : 0,
      visualSettingBadPoints: Array.isArray(analysisData?.visualSettingBadPoints) ? analysisData.visualSettingBadPoints.length : 0,
      visualSettingSuggestions: Array.isArray(analysisData?.visualSettingSuggestions) ? analysisData.visualSettingSuggestions.length : 0,
      videoFileId: videoFile?.id,
      videoFileThumbnailKey: videoFile?.thumbnailKey,
      thumbnailKeyOverride: thumbnailKey,
      finalThumbnailUrl: thumbnailUrl,
      totalFiles: analysisData?.files?.length || 0
    }
  });

  // Determine upload status
  const getUploadStatus = () => {
    if (!analysisData?.files) return { status: 'unknown', hasVideo: false, isAudioOnly: false };
    
    const videoFiles = analysisData.files.filter((f: any) => 
      f.key && !f.key.includes('-audio-encrypted') && 
      (f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov') || f.type?.startsWith('video/'))
    );
    
    const audioFiles = analysisData.files.filter((f: any) => 
      f.key && (f.key.includes('-audio-encrypted') || f.type?.startsWith('audio/'))
    );

    const hasVideo = videoFiles.length > 0;
    const hasAudio = audioFiles.length > 0;
    const overallStatus = analysisData.status;

    // If we have audio but no video, check if video might still be uploading
    if (hasAudio && !hasVideo) {
      // If analysis is still pending/processing, video might be uploading
      if (overallStatus === 'pending' || overallStatus === 'processing') {
        return { status: 'uploading', hasVideo: false, isAudioOnly: false };
      } else {
        // Analysis is complete but no video - this is audio-only
        return { status: 'audio-only', hasVideo: false, isAudioOnly: true };
      }
    }

    return { 
      status: hasVideo ? 'completed' : 'unknown', 
      hasVideo, 
      isAudioOnly: false 
    };
  };

  const uploadStatus = getUploadStatus();
  getLogger().info('VideoSetting upload status check', {
    metadata: {
      uploadStatus,
      filesCount: analysisData?.files?.length || 0,
      analysisStatus: analysisData?.status
    }
  });

  const analyzeVisualSetting = async () => {
    if (!thumbnailUrl) {
      setError('No thumbnail available for analysis');
      return;
    }

    setIsLoading(true);
    setError('');
    setHasAnalyzed(false); // Reset for retry
    
    try {
      const response = await fetch('/api/analyze-visual-setting', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          thumbnailUrl,
          analysisId: analysisData?.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze visual setting');
      }

      const data = await response.json();
      setVisualAnalysis(data);
      setHasAnalyzed(true);
    } catch (err) {
      setError('Failed to analyze visual setting. Please try again.');
      setHasAnalyzed(true); // Prevent infinite retries
      console.error('Visual analysis error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load existing analysis or trigger new analysis
  useEffect(() => {
    // Skip if we already have analysis loaded and analyzed
    if (hasAnalyzed && visualAnalysis) {
      getLogger().info('VideoSetting already analyzed, skipping useEffect');
      return;
    }
    
    getLogger().info('VideoSetting useEffect conditions', {
      metadata: {
        existingVisualAnalysis: !!existingVisualAnalysis,
        thumbnailUrl: !!thumbnailUrl,
        hasAnalyzed,
        isLoading,
        error: !!error,
        visualSettingAnalyzedAt: analysisData?.visualSettingAnalyzedAt,
        shouldTrigger: !existingVisualAnalysis && thumbnailUrl && !hasAnalyzed && !isLoading && !error
      }
    });
    
    if (existingVisualAnalysis && !visualAnalysis) {
      // Load existing analysis from database only if we don't have it yet
      getLogger().info('VideoSetting loading existing analysis from database');
      setVisualAnalysis({
        goodPoints: existingVisualAnalysis.goodPoints || [],
        badPoints: existingVisualAnalysis.badPoints || [],
        suggestions: existingVisualAnalysis.suggestions || [],
      });
      setHasAnalyzed(true);
      return; // Early return to prevent further execution
    }
    
    if (!existingVisualAnalysis && thumbnailUrl && !hasAnalyzed && !isLoading && !error) {
      // Only trigger new analysis if no existing data in database and we have a thumbnail
      getLogger().info('VideoSetting triggering analyzeVisualSetting');
      analyzeVisualSetting();
      return; // Early return to prevent further execution
    }
    
    if (!existingVisualAnalysis && !thumbnailUrl && !hasAnalyzed && !isLoading && !error) {
      // Try to generate thumbnail for video file if it doesn't exist
      if (videoFile && !hasAttemptedThumbnailGeneration.current) {
        hasAttemptedThumbnailGeneration.current = true;
        getLogger().info('VideoSetting no thumbnail found, trying to generate one', {
          metadata: { 
            videoFileId: videoFile.id,
            videoFileKey: videoFile.key,
            videoFileType: videoFile.type,
            currentThumbnailKey: videoFile.thumbnailKey
          }
        });
        // Try to generate thumbnail via API call
        fetch('/api/generate-thumbnail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileId: videoFile.id }),
        }).then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            if (data.thumbnailKey) {
              getLogger().info('VideoSetting thumbnail generated successfully', {
                metadata: { thumbnailKey: data.thumbnailKey }
              });
              // The component will re-render when the parent updates with new thumbnail data
            }
          } else {
            getLogger().warn('VideoSetting thumbnail generation failed', {
              metadata: { status: response.status }
            });
          }
        }).catch((error) => {
          getLogger().error('VideoSetting thumbnail generation error', {
            metadata: { error: error instanceof Error ? error.message : String(error) }
          });
        });
      }
    }
  }, [analysisData?.visualSettingAnalyzedAt, analysisData?.id, thumbnailUrl, analysisData?.files]); // Added files dependency

  // Show uploading state when video is still being uploaded
  if (uploadStatus.status === 'uploading') {
    return null;

    // return (
    //   <Card id="visual-setting" className="border-2 border-brand-dark-blue/30">
    //     <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5">
    //       <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
    //         <Eye className="h-6 w-6 text-brand-dark-blue" />
    //         Visual Setting Analysis
    //       </CardTitle>
    //       <CardDescription className="text-brand-dark-blue/70 font-medium">
    //         AI analysis of your video setup, lighting, framing, and background
    //       </CardDescription>
    //     </CardHeader>
    //     <CardContent>
    //       <div className="text-center py-8">
    //         <div className="flex items-center justify-center gap-2 mb-4">
    //           <RefreshCw className="h-6 w-6 animate-spin text-brand-gold" />
    //           <span className="text-lg font-medium text-brand-dark-blue">Video Uploading...</span>
    //         </div>
    //         <p className="text-brand-dark-blue/70 text-sm">
    //           Your video is still being uploaded. Visual analysis will begin once the upload is complete.
    //         </p>
    //         <p className="text-brand-dark-blue/50 text-xs mt-2">
    //           This usually takes a few moments depending on your video size and connection speed.
    //         </p>
    //       </div>
    //     </CardContent>
    //   </Card>
    // );
  }

  // Show audio-only state when no video will be available AND no existing analysis
  if ((uploadStatus.isAudioOnly || (!thumbnailUrl && uploadStatus.status === 'audio-only')) && !existingVisualAnalysis) {
    getLogger().info('VideoSetting showing audio-only not available message', {
      metadata: { reason: 'no video content and no existing analysis' }
    });
    return null;
    // return (
    //   <Card id="visual-setting" className="border-2 border-brand-dark-blue/30">
    //     <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5">
    //       <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
    //         <Eye className="h-6 w-6 text-brand-dark-blue" />
    //         Visual Setting Analysis
    //       </CardTitle>
    //       <CardDescription className="text-brand-dark-blue/70 font-medium">
    //         AI analysis of your video setup, lighting, framing, and background
    //       </CardDescription>
    //     </CardHeader>
    //     <CardContent>
    //       <div className="text-center py-8">
    //         <Eye className="h-12 w-12 mx-auto text-brand-dark-blue/30 mb-4" />
    //         <p className="text-brand-dark-blue/70 text-sm">
    //           Visual setting analysis is not available for audio-only recordings.
    //         </p>
    //         <p className="text-brand-dark-blue/50 text-xs mt-2">
    //           Record with video to get feedback on your setup, lighting, and framing.
    //         </p>
    //       </div>
    //     </CardContent>
    //   </Card>
    // );
  }

  if (!thumbnailUrl) {
    // Show component but with message explaining why visual analysis isn't available
    getLogger().info('VisualSetting render state', {
      metadata: { reason: 'no thumbnail available' }
    });
    return null;
    // return (
    //   <Card id="visual-setting" className="border-2 border-brand-dark-blue/30">
    //     <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5">
    //       <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
    //         <Eye className="h-6 w-6 text-brand-dark-blue" />
    //         Visual Setting Analysis
    //       </CardTitle>
    //       <CardDescription className="text-brand-dark-blue/70 font-medium">
    //         AI analysis of your video setup, lighting, framing, and background
    //       </CardDescription>
    //     </CardHeader>
    //     <CardContent>
    //       <div className="text-center py-8">
    //         <Eye className="h-12 w-12 mx-auto text-brand-dark-blue/30 mb-4" />
    //         <p className="text-brand-dark-blue/70 text-sm">
    //           Visual setting analysis is not available - no thumbnail found.
    //         </p>
    //         <p className="text-brand-dark-blue/50 text-xs mt-2">
    //           This usually means the video is still processing or there was an issue with video upload.
    //         </p>
    //       </div>
    //     </CardContent>
    //   </Card>
    // );
  }

  return (  <Card id="text">
    <CardHeader>
            <CardTitle className="flex items-center gap-2">
            <Eye className="h-6 w-6 text-brand-dark-blue" />
            <Trans>Visual Setting</Trans>
            </CardTitle>
            <CardDescription>
            <Trans>AI analysis of your video setup, lighting, framing, and background</Trans>
            </CardDescription>
          </CardHeader>
     <CardContent className="space-y-6">
          {/* Thumbnail Display */}
          <div className="flex">
            <div className="relative max-w-md w-full">
              <img
                src={thumbnailUrl}
                alt="Video thumbnail"
                className="w-full h-auto rounded-lg border-2 border-brand-dark-blue/20 shadow-lg"
                onError={() => setError('Failed to load thumbnail')}
              />
              {isLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <div className="bg-white/90 p-4 rounded-lg flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin text-brand-dark-blue" />
                    <span className="text-sm font-medium text-brand-dark-blue"><Trans>Analyzing...</Trans></span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Analysis Results */}
          {error && (
            <div className="text-center py-4">
              <p className="text-brand-error text-sm mb-2">{error || 'Analysis failed'}</p>
              <Button
                onClick={analyzeVisualSetting}
                size="sm"
                variant="outline"
                className="text-brand-dark-blue border-brand-dark-blue hover:bg-brand-dark-blue/5"
              >
                <Trans>Try Again</Trans>
              </Button>
            </div>
          )}

          {visualAnalysis && (
            <div className="space-y-4">
              {/* Good Points */}
              {Array.isArray(visualAnalysis.goodPoints) && visualAnalysis.goodPoints.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-success flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <Trans>What's Working Well</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.goodPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-brand-success shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Areas for Improvement */}
              {Array.isArray(visualAnalysis.badPoints) && visualAnalysis.badPoints.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-error/20 bg-gradient-to-br from-brand-error/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-error flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    <Trans>Areas for Improvement</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.badPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-error shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {Array.isArray(visualAnalysis.suggestions) && visualAnalysis.suggestions.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-gold/20 bg-gradient-to-br from-brand-gold/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-gold flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    <Trans>Recommendations</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Star className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!visualAnalysis && !isLoading && !error && (
            <div className="text-center py-8">
              <Button
                onClick={analyzeVisualSetting}
                className="bg-brand-dark-blue text-brand-white hover:bg-brand-dark-blue/90 font-semibold"
              >
                <Eye className="h-4 w-4 mr-2" />
                <Trans>Analyze Visual Setting</Trans>
              </Button>
            </div>
          )}
        </CardContent>
  </Card>)


 {/*
  return (
    <Card id="visual-setting" className="border-2 border-brand-dark-blue/30">
      <CardHeader className="bg-gradient-to-r from-brand-dark-blue/10 to-brand-gold/5">
        <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
          <Eye className="h-6 w-6 text-brand-dark-blue" />
          Visual Setting Analysis
        </CardTitle>
        <CardDescription className="text-brand-dark-blue/70 font-medium">
          AI analysis of your video setup, lighting, framing, and background
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
         
          <div className="flex mt-4">
            <div className="relative max-w-md w-full">
              <img
                src={thumbnailUrl}
                alt="Video thumbnail"
                className="w-full h-auto rounded-lg border-2 border-brand-dark-blue/20 shadow-lg"
                onError={() => setError('Failed to load thumbnail')}
              />
              {isLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <div className="bg-white/90 p-4 rounded-lg flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin text-brand-dark-blue" />
                    <span className="text-sm font-medium text-brand-dark-blue"><Trans>Analyzing...</Trans></span>
                  </div>
                </div>
              )}
            </div>
          </div>

         
          {error && (
            <div className="text-center py-4">
              <p className="text-brand-error text-sm mb-2">{error || 'Analysis failed'}</p>
              <Button
                onClick={analyzeVisualSetting}
                size="sm"
                variant="outline"
                className="text-brand-dark-blue border-brand-dark-blue hover:bg-brand-dark-blue/5"
              >
                <Trans>Try Again</Trans>
              </Button>
            </div>
          )}

          {visualAnalysis && (
            <div className="space-y-4">
           
              {visualAnalysis.goodPoints.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-success flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <Trans>What's Working Well</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.goodPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-brand-success shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

           
              {visualAnalysis.badPoints.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-error/20 bg-gradient-to-br from-brand-error/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-error flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    <Trans>Areas for Improvement</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.badPoints.map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-error shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            
              {visualAnalysis.suggestions.length > 0 && (
                <div className="p-4 rounded-lg border-2 border-brand-gold/20 bg-gradient-to-br from-brand-gold/5 to-transparent">
                  <h4 className="text-sm font-bold mb-3 text-brand-gold flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    <Trans>Recommendations</Trans>
                  </h4>
                  <ul className="space-y-2">
                    {visualAnalysis.suggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Star className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-dark-blue font-medium">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!visualAnalysis && !isLoading && !error && (
            <div className="text-center py-8">
              <Button
                onClick={analyzeVisualSetting}
                className="bg-brand-dark-blue text-brand-white hover:bg-brand-dark-blue/90 font-semibold"
              >
                <Eye className="h-4 w-4 mr-2" />
                <Trans>Analyze Visual Setting</Trans>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  */}
}
